// The business's payment QR (docs/plans/payment-qr.md): a static code from a bank
// or wallet, shown at checkout. The app keeps what the code says and redraws it;
// it never learns whether a payment arrived.
import type { PaymentMethod } from '@ims/data'

/** The server's limit on the stored text (tenant_settings.payment_qr). */
export const PAYMENT_QR_MAX = 2000
export const PAYMENT_QR_LABEL_MAX = 40
export const PAYMENT_QR_PAYEE_MAX = 80

export type PaymentQrMethod = Extract<PaymentMethod, 'wallet' | 'bank'>

export interface PaymentQr {
  payload: string
  /** Shown above the code: "Fonepay". */
  label: string
  /** Shown under it, so the customer can check who they are paying. */
  payee: string | null
  method: PaymentQrMethod
}

/** The QR a business has set, or null. */
export function paymentQrOf(
  settings:
    | {
        payment_qr: string | null
        payment_qr_label: string | null
        payment_qr_payee: string | null
        payment_qr_method: PaymentQrMethod
      }
    | null
    | undefined,
): PaymentQr | null {
  if (!settings?.payment_qr) return null
  return {
    payload: settings.payment_qr,
    label: settings.payment_qr_label?.trim() || 'QR',
    payee: settings.payment_qr_payee?.trim() || null,
    method: settings.payment_qr_method,
  }
}

/**
 * The fields of an EMV merchant QR (the format NepalPay and many bank codes use):
 * two digits of tag, two of length, then the value. Null when the text is not one.
 */
export function emvFields(payload: string): Map<string, string> | null {
  if (!payload.startsWith('000201')) return null
  const fields = new Map<string, string>()
  let at = 0
  while (at < payload.length) {
    const head = payload.slice(at, at + 4)
    if (!/^\d{4}$/.test(head)) return null
    const length = Number(head.slice(2))
    const value = payload.slice(at + 4, at + 4 + length)
    if (value.length !== length) return null
    fields.set(head.slice(0, 2), value)
    at += 4 + length
  }
  return fields
}

const KNOWN: [RegExp, string][] = [
  [/fonepay/i, 'Fonepay'],
  [/esewa/i, 'eSewa'],
  [/khalti/i, 'Khalti'],
  [/imepay/i, 'IME Pay'],
  [/nepalpay|nchl|connectips/i, 'NepalPay'],
]

export type PaymentQrRead =
  | { ok: true; payload: string; label: string; payee: string | null }
  | { ok: false; message: string }

/**
 * Decides whether what the camera or a photo read can be kept as the payment QR, and
 * guesses its name and payee from it (both are only suggestions the owner can change).
 */
export function readPaymentQr(text: string): PaymentQrRead {
  const payload = text.trim()
  if (!payload) return { ok: false, message: 'This QR is empty.' }
  // The scanner also reads the barcodes on packs.
  if (/^\d{6,14}$/.test(payload)) {
    return { ok: false, message: 'That is a product barcode, not a payment QR.' }
  }
  // Redrawing only works for text: bytes that were not text come back as U+FFFD.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u0008\u000b\u000c\u000e-\u001f�]/.test(payload)) {
    return { ok: false, message: 'This QR holds data the app cannot redraw.' }
  }
  if (payload.length > PAYMENT_QR_MAX) {
    return { ok: false, message: 'This QR holds too much to keep.' }
  }
  const label = KNOWN.find(([pattern]) => pattern.test(payload))?.[1] ?? ''
  const name = emvFields(payload)?.get('59')?.trim()
  return {
    ok: true,
    payload,
    label,
    payee: name ? name.slice(0, PAYMENT_QR_PAYEE_MAX) : null,
  }
}
